import React, { useState, useEffect, useMemo } from "react";
import "./distribution.less";
import { dataMap } from "@/constants/distribution";
import { rebuildFormula } from "@/config/func";
import materialsDB from "@/constants/materialsDB";
import ResultDataTable from "./DistributionResultDataTable";
import TowerEffect from "@/Components/TowerEffect";
import EffCharts from "@/Components/TowerEffect/EffCharts";
import ShowBtns from "./DistributionShowBtn";
import { Modal, Table, Select } from "antd";

const formatToSignificantDigits = (value: number, digits: number = 4): string => {
  if (value === 0) return "0";
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const factor = Math.pow(10, digits - 1 - exponent);
  return (Math.round(value * factor) / factor).toString();
};


const Distribution = (props: any) => {
  const { inputs, transInputs, multiTaskResults } = props;
  console.log("multiTaskResults:",multiTaskResults);
const [showMode, setShowMode] = useState<"chart" | "table">("chart");
  const [currentMode, setCurrentMode] = useState<string>("P");
  const [currentMaterial, setCurrentMaterial] = useState<any>(null);
  const [currentData, setCurrentData] = useState<any>(null);
 
  const [modalStatus, setModalStatus] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<React.ReactNode>("");
  const [modalContent, setModalContent] = useState<React.ReactNode>("");

  // 🔺 多任务数据：每个任务对应一条曲线/一个塔图
  const validMultiTasks = useMemo(() => {
    if (!multiTaskResults || !Array.isArray(multiTaskResults)) return [];
    return multiTaskResults.filter(t => t.result && Object.keys(t.result).length > 0);
  }, [multiTaskResults]);

  // 🔺 多任务模式下，当前选中的任务（弹窗/单塔图时使用）
  const [activeTaskId, setActiveTaskId] = useState<string>(() => 
    validMultiTasks[0]?.taskId || ""
  );

  // ======================
  // 数据处理：多任务模式数据聚合
  // ======================
  const multiTaskChartData = useMemo(() => {
    if (!validMultiTasks.length) return [];

    // 1. 非组分数据（T/P/V/L 等）
    if (!["X", "Y", "K"].includes(currentMode)) {
      return validMultiTasks.map(task => ({
        taskId: task.taskId,
        taskName: task.taskName,
        data: task.result?.[currentMode] || [],
      }));
    }
    // 2. 组分数据（X/Y/K）
    else {
      if (!currentMaterial) return [];
      return validMultiTasks.map(task => {
        const tmp = task.result?.[currentMode];
        if (!tmp || !Array.isArray(tmp)) return [];
        const componentData = tmp.map((item: any) => {
          if (Array.isArray(item) && currentMaterial.index >= 0 && currentMaterial.index < item.length) {
            return item[currentMaterial.index];
          }
          return null;
        });
        return {
          taskId: task.taskId,
          taskName: task.taskName,
          data: componentData.filter(v => v !== null),
        };
      });
    }
  }, [validMultiTasks, currentMode, currentMaterial]);

  // 当前选中任务的原始数据（弹窗/塔图使用）
  const activeTaskResult = useMemo(() => {
    const task = validMultiTasks.find(t => t.taskId === activeTaskId);
    return task?.result || null;
  }, [validMultiTasks, activeTaskId]);

  // 监听组分模式，设置默认组分（使用第一个任务的物料）
  useEffect(() => {
    if (["X", "Y", "K"].includes(currentMode) && inputs?.materials?.length > 0) {
      const methanol = inputs.materials.find((item: any) => item.cas === "67-56-1");
      setCurrentMaterial(methanol || inputs.materials[0]);
    }
  }, [currentMode, inputs?.materials]);

const buildModalSource = () => {
  if (currentData === null) return;

  const trayIndex = currentData;

  const title = (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ fontSize: 16, fontWeight: 500 }}>
        第 {Number(trayIndex) + 1} 块塔板 · 多任务对比
      </span>
      <Select
        value={currentData}
        onChange={(val) => setCurrentData(val)}
        style={{ width: 160 }}
      >
        {validMultiTasks[0]?.result?.[currentMode]?.map((_, index) => (
          <Select.Option key={index} value={index}>
            塔板 {index + 1}
          </Select.Option>
        ))}
      </Select>
    </div>
  );

  // ============================
  // 1. 基础参数对比（保留原有格式）
  // ============================
  const baseParams = [
    { key: "P", label: "压力", unit: "Pa" },
    { key: "T", label: "温度", unit: "K" },
    { key: "V", label: "气相摩尔流量", unit: "mol/h" },
    { key: "L", label: "液相摩尔流量", unit: "mol/h" },
    { key: "HV", label: "气相焓", unit: "J/mol" },
    { key: "HL", label: "液相焓", unit: "J/mol" },
  ];

  // ============================
  // 2. 组分数据：改成中文标签版本
  // ============================
  const content = (
    <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
      {/* 基础参数对比 */}
      <h4 style={{ marginBottom: 10 }}>⚙️ 基础参数对比（所有任务）</h4>
      <Table
        size="small"
        bordered
        pagination={false}
        columns={[
          {
            title: "参数",
            dataIndex: "label",
            width: 150,
            fixed: "left" as const,
          },
          ...validMultiTasks.map(task => ({
            title: task.taskName,
            dataIndex: `task_${task.taskId}`,
            align: "center" as const,
          })),
        ]}
        dataSource={baseParams.map((item) => {
          const row: any = { key: item.key, label: `${item.label}(${item.unit})` };
          validMultiTasks.forEach((task) => {
            const val = task.result?.[item.key]?.[trayIndex] ?? "-";
            row[`task_${task.taskId}`] = val === "-" ? "-" : formatToSignificantDigits(Number(val));
          });
          return row;
        })}
      />

      {/* 组分数据对比（中文标签版） */}
      <h4 style={{ margin: "20px 0 10px" }}>🧪 组分对比（所有任务）</h4>
      <Table
        size="small"
        bordered
        pagination={false}
        scroll={{ x: "max-content" }}
        columns={[
          {
            title: "组分",
            dataIndex: "materialName",
            width: 220,
            fixed: "left" as const,
          },
          ...validMultiTasks.map((task) => ({
            title: task.taskName,
            dataIndex: `task_${task.taskId}`,
            align: "center" as const,
          })),
        ]}
        dataSource={inputs.materials.map((material, matIndex) => {
          const row: any = {
            key: matIndex,
            materialName: `${rebuildFormula(material.Molecular_formula)}（${material.Name_ZH}）`,
          };

          validMultiTasks.forEach((task) => {
            const res = task.result;
            const tray = trayIndex;
            // 使用对应任务的物料数据
            const taskMaterial = task.input?.materials?.[matIndex] || material;

            // 1. 液相摩尔分率
            const x = res?.X?.[tray]?.[matIndex] ?? "-";
            const xDisplay = x === "-" ? "-" : formatToSignificantDigits(Number(x));

            // 2. 汽相摩尔分率
            const y = res?.Y?.[tray]?.[matIndex] ?? "-";
            const yDisplay = y === "-" ? "-" : formatToSignificantDigits(Number(y));

            // 3. 相平衡常数
            const k = res?.K?.[tray]?.[matIndex] ?? "-";

            // 4. 摩尔流量
            const liq = res?.L?.[tray] ?? 0;
            const vap = res?.V?.[tray] ?? 0;
            let molFlow = "-";
            if (x !== "-") {
              molFlow = formatToSignificantDigits(Number(x) * liq);
            } else if (y !== "-") {
              molFlow = formatToSignificantDigits(Number(y) * vap);
            }

            // 5. 质量流量
            let massFlow = "-";
            if (x !== "-") {
              massFlow = formatToSignificantDigits(Number(x) * liq * taskMaterial.MW / 1000);
            } else if (y !== "-") {
              massFlow = formatToSignificantDigits(Number(y) * vap * taskMaterial.MW / 1000);
            }

            // 6. 质量浓度
            const t = res?.T?.[tray] ?? 298.15;
            const p = res?.P?.[tray] ?? 101325;
            let massCon = "-";
            if (x !== "-") {
              massCon = formatToSignificantDigits((Number(x) * p * taskMaterial.MW) / (8.314 * t));
            } else if (y !== "-") {
              massCon = formatToSignificantDigits((Number(y) * p * taskMaterial.MW) / (8.314 * t));
            }

            // 🔴 这里已经把 x/y/K 换成了中文
            row[`task_${task.taskId}`] = (
              <div style={{ lineHeight: "1.6", textAlign: "left", padding: "4px 8px" }}>
                <div>液相摩尔分率: {xDisplay}</div>
                <div>汽相摩尔分率: {yDisplay}</div>
                <div>相平衡常数: {k}</div>
                <div>摩尔流量: {molFlow} mol/h</div>
                <div>质量流量: {massFlow} g/h</div>
                <div>质量浓度: {massCon} mg/Nm³</div>
              </div>
            );
          });

          return row;
        })}
      />
    </div>
  );

  setModalTitle(title);
  setModalContent(content);
};

  useEffect(() => {
    if (currentData !== null && activeTaskResult) {
      buildModalSource();
    }
  }, [currentData, activeTaskResult, currentMaterial]);

  // 计算多任务数据的最大/最小值，用于图表坐标轴
  const getMultiTaskRange = () => {
    if (!multiTaskChartData.length) return { min: 0, max: 1 };
    const allValues: number[] = [];
    multiTaskChartData.forEach(task => {
      task.data.forEach((v: any) => {
        if (!isNaN(Number(v)) && isFinite(Number(v))) allValues.push(Number(v));
      });
    });
    if (allValues.length === 0) return { min: 0, max: 1 };
    const max = Math.max(...allValues);
    const min = Math.min(...allValues);
    const range = Math.abs(max - min);
    return {
      min: min >= 0 ? Math.max(0, min - range * 0.1) : min - range * 0.1,
      max: max + range * 0.1,
    };
  };

  const chartRange = getMultiTaskRange();
  const isLoading = !multiTaskChartData || multiTaskChartData.length === 0;

  const getChartTitle = () => {
    if (["K", "X", "Y"].includes(currentMode) && currentMaterial) {
      return `${rebuildFormula(currentMaterial?.Molecular_formula)}${dataMap[currentMode]?.title || ""} 多任务对比`;
    } else {
      return `${dataMap[currentMode]?.title || currentMode}分布图（多任务对比）`;
    }
  };

  const showMF = (item: any) => {
    const mat = materialsDB.find(i => i.cas === item.cas);
    return mat ? rebuildFormula(mat.Molecular_formula) : rebuildFormula(item.Molecular_formula || item.name);
  };

 // ...其他代码不变
return (
  <div className="single-result-container">
    <div className="show-btns-wrapper">
                <ShowBtns
                    currentMode={currentMode}
                    setCurrentMode={setCurrentMode}
                    showMode={showMode}
                    setShowMode={setShowMode}
                    setCurrentData={setCurrentData}
                />
            </div>
    {isLoading ? (
      <div className="loading-area">
        <p>正在加载数据...</p>
        <p className="loading-mode">当前模式: {currentMode}</p>
      </div>
    ) : (
      <div className="main-content-wrapper multi-task-mode">
        {/* 🔺 改为自适应的塔图容器 */}
        <div className="multi-task-towers-container">
  {validMultiTasks.map((task, index) => (
    <div 
      key={task.taskId} 
      className="single-tower-wrapper"
      onClick={() => setActiveTaskId(task.taskId)}
      style={{ opacity: 1 }}
    >
      <div className="task-name-label">{task.taskName}</div>
      {/* 🔺 修改：这里是关键，动态传递组分数据 */}
      <TowerEffect
        currentMode={currentMode}
        setModalStatus={setModalStatus}
        currentData={currentData}
        setCurrentData={setCurrentData}
        currentModeData={
          ["X", "Y", "K"].includes(currentMode) && currentMaterial
            ? task.result?.[currentMode]?.map((trayData: any[]) => trayData?.[currentMaterial.index]) || []
            : task.result?.[currentMode] || []
        }
        wrapWidth={280}
        trayHeight={310}
        imageStyle={{ height: "420px", width: "80px" }}
        trayContainerStyle={{ top: "40px", left: "27px", width: "47px" }}
      />
    </div>
  ))}
</div>

        <div className="data-show-area">
          <div className="line-chart-container">
            {["X", "Y", "K"].includes(currentMode) && inputs?.materials && (
              <div className="materials-select-container">
                {inputs.materials.map((i: any, index) => {
                  const isActive = currentMaterial?.cas === i.cas;
                  return (
                    <div key={index} className="material-item">
                      <button
                        className={`material-btn ${isActive ? "active" : ""}`}
                        onClick={() => setCurrentMaterial({ ...i, index })}
                      >
                        <span className={`material-btn-text ${isActive ? "active" : ""}`}>
                          {showMF(i)}
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
              <EffCharts
                multiTaskData={multiTaskChartData}
                title={getChartTitle()}
                currentMode={currentMode}
                height={470}
                min={chartRange.min}
                max={chartRange.max}
              />
          </div>
        </div>
      </div>
    )}

    <Modal
      title={modalTitle}
      open={modalStatus}
      onCancel={() => {
        setModalStatus(false);
        setCurrentData(null);
      }}
      width={1400}
      destroyOnClose
    >
      {modalContent}
    </Modal>
  </div>
);
};

export default Distribution;